import * as React from "react";
import { FixedCommentMutation, TestAcceptabilityMutation, OverreadStatusEnum, ModelError, Permission, SessionMetadata, OverreadStatusIdEnum, OverreadChallengeStatusEnum } from "../../misc/api/apiTypes";
import { LungVolumeResultsTable } from "./LungVolumeResultsTable";
import { Navigate } from "react-router";
import { useFixedComments, ToggleSessionFixedCommentsVisibilityAction } from "../Shared/UseFixedComments";
import FileAttachmentsTable from "./../../file-attachments/components/viewer/FileAttachmentsTable";
import { FileAttachmentsTableMode } from "./../../file-attachments/models";
import NotesPanel from "../Shared/NotesPanel";
import LoadingWithMessage from "./../../misc/components/LoadingWithMessage";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import SessionMetadataTable from "../Shared/SessionMetadataTable";
import AcceptabilityDashboard from "../Shared/AcceptabilityDashboard";
import { useAcceptability, LoadingBestValuesAction, UpdateBestTestAction, UpdateRepeatabilityAction, DoneLoadingBestValuesAction } from '../Shared/UseAcceptability';
import { LungVolumeAnalysisSetTypeEnum } from "../../misc/api/ApiLungVolumeAnalysisSetType";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import { LungVolumeAnalysisSet, LungVolumeSession, SessionNote, WebPortalClient } from "webportalclient";
import { PFTResults, LungVolumeResults, StudyLungVolumeRelevantParameters } from "../Shared/PFTAPITypes";
import { useSecurityContext } from "../../Core/SecurityContext";
import { ChallengeAlert } from "../Shared/ChallengeAlert";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { GoBackToList } from "../Shared/AnalysisHelpers";
import { LoadAnalysisSetSelectionsAction, useNextGenFixedComments } from "../Spirometry/Testing/UseNextGenFixedComments";
import { FixedCommentSetTypeEnum } from "../../misc/api/FixedComments/FixedCommentSetTypes";
import { Authorization } from "../../misc/components/Authorization";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { useNavigate, useParams } from "react-router-dom";
import { ModalSize } from '../../misc/components/modal';

export interface LungVolumeAnalysisScreenProps {
    currentPermissions: Permission[];
}

export interface LungVolumeAnalysisScreenRouteProps
    extends 
    LungVolumeAnalysisScreenProps { }

enum ViewState {
    View = "view",
    Overread = "overread"
}

interface ExpectedMatchParams {
    analysisSetId: string;
    viewState: ViewState;
}

export const LungVolumeAnalysisScreen = () => {
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const { analysisSetId, viewState } = useParams();
    const navigate = useNavigate();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const [sessionMetadata, setSessionMetadata] = React.useState<SessionMetadata>(null);
    const [overreadAnalysisSet, setOverreadAnalysisSet] = React.useState<LungVolumeAnalysisSet>();
    const [session, setSession] = React.useState<LungVolumeSession>();
    const [sessionType, setSessionType] = React.useState<SessionTypes>(null);
    const [analysisSet, setAnalysisSet] = React.useState<LungVolumeResults>(null);
    const [sessionNotes, setSessionNotes] = React.useState<SessionNote[]>(null);
    const [isViewOnly, setIsViewOnly] = React.useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [isAnalysisRejected, setIsAnalysisRejected] = React.useState<boolean>(false);
    const [studyLungVolumeRelevantParameters, setStudyLungVolumeRelevantParameters] = React.useState<StudyLungVolumeRelevantParameters>(null);
    const [noteError, setNoteError] = React.useState<string | undefined>();
    const [error, setError] = React.useState<string | undefined>();
    const { study } = useCurrentStudyConfigurationContext();

    const fetchSessionNotes = async (sessionId: string) => {
        const notes = await client.client.getSessionNotes(undefined, `sessionId eq ${sessionId}`);
        setSessionNotes(notes.value);
    }

    React.useEffect(() => {
        const fetchAnalysisSet = async () => {
            const access = await client.client.getLungVolumeAnalysisSet(analysisSetId, "canEdit");

            setIsViewOnly(!access.canEdit);
            const overreadPermission = currentUserPermissions.find(p => p.Id == PermissionEnum.OverreadLungVolumeSession);
            setIsViewOnly(!overreadPermission);

            if (access.canEdit && viewState === ViewState.Overread && overreadPermission) {
                setIsViewOnly(false);
                await legacyClient.startOverreadingLungVolume(analysisSetId);
            } else {
                setIsViewOnly(true);
            }

            const results = await legacyClient.getLungVolumeAnalysisSetResults(analysisSetId);
            setAnalysisSet(results);

            const sessionMetadataResponse = await legacyClient.getLungVolumeSession(results.SessionId);
            setSessionMetadata(sessionMetadataResponse);

            const currentAnalysisSet = sessionMetadataResponse.AnalysisSets.find(a => a.Id === analysisSetId);
            const isQc = currentAnalysisSet.Type.Id === LungVolumeAnalysisSetTypeEnum.QC;

            const overreadAnalysisSetId = viewState === ViewState.Overread || isQc ? analysisSetId : sessionMetadataResponse.AnalysisSets.find(a => a.Type.Id === LungVolumeAnalysisSetTypeEnum.Overread).Id;
            const overreadAnalysisSetResult = await client.client.getLungVolumeAnalysisSet(overreadAnalysisSetId, undefined, "tests");
            setOverreadAnalysisSet(overreadAnalysisSetResult);

            const sessionResult = await client.client.getLungVolumeSession(sessionMetadataResponse.Id, undefined, "tests");
            setSession(sessionResult);

            if (sessionMetadataResponse.SessionTypeId === SessionTypes.LungVolumeHelium) {
                setSessionType(SessionTypes.LungVolumeHelium);
            }
            else {
                setSessionType(SessionTypes.LungVolumePleth);
            }

            const parameters = await legacyClient.getStudyLungVolumeRelevantParameters();

            setStudyLungVolumeRelevantParameters(parameters);

            const isRejected = sessionMetadataResponse.AnalysisSets.find(a => a.Id === analysisSetId).Status.Id === OverreadStatusIdEnum.Rejected;
            setIsAnalysisRejected(isRejected);

            await fetchSessionNotes(results.SessionId);

            fixedCommentDispatchOverreading(LoadAnalysisSetSelectionsAction.create(overreadAnalysisSetResult.fixedComments, overreadAnalysisSetResult.tests));
        }

        fetchAnalysisSet();
    }, [analysisSetId, viewState]);

    const [shouldValidateFixedComments, setShouldValidateFixedComments] = React.useState(false);

    const [fixedCommentStateOverreading, fixedCommentDispatchOverreading] = useNextGenFixedComments(sessionType, FixedCommentSetTypeEnum.Overread, overreadAnalysisSet);
    const [acceptabilityState, acceptabilityDispatch] = useAcceptability(analysisSet);

    React.useEffect(() => {

        const updateBestTestAndRepeatability = async () => {
            if (acceptabilityState.spirometryResults.TestResults.length) {

                acceptabilityDispatch(LoadingBestValuesAction.create());

                const lungVolumeRepeatabilityValues = await legacyClient.calculateLungVolumeRepeatability(sessionMetadata.Id, acceptabilityState.spirometryResults.TestResults);
                const lungVolumeBestTestValues = sessionType === SessionTypes.LungVolumeHelium ? await legacyClient.calculateLungVolumeHeBestTests(acceptabilityState.spirometryResults.TestResults) :
                    await legacyClient.calculateLungVolumePlethBestTests(acceptabilityState.spirometryResults.TestResults);

                acceptabilityDispatch(UpdateBestTestAction.create(lungVolumeBestTestValues));
                acceptabilityDispatch(UpdateRepeatabilityAction.create(lungVolumeRepeatabilityValues));

                acceptabilityDispatch(DoneLoadingBestValuesAction.create());
            }
        }
        updateBestTestAndRepeatability();
    }, [acceptabilityState.changedAcceptability, acceptabilityDispatch]);

    if (analysisSet === null || sessionMetadata === null || studyLungVolumeRelevantParameters === null || sessionNotes == null) {
        return (<LoadingWithMessage message="Loading Lung Volume Data..." />);
    }

    const goBackToList = () => GoBackToList(navigate, currentUserPermissions, "/LungVolume", PermissionEnum.OverreadLungVolumeSession, "/LungVolume/Analysis");

    const setAnalysisDecision = async (annotation: string, decision: OverreadStatusEnum) => {
        setIsSubmitting(true);
        setError(undefined);
        setIsAnalysisRejected(decision === OverreadStatusEnum.Rejected);

        const sessionFixedCommentsMutation = fixedCommentStateOverreading.sessionFixedComments.comments.map(x => ({ CommentId: x.id, IsSelected: x.selected } as FixedCommentMutation));
        const testFixedCommentsMutation = fixedCommentStateOverreading.testFixedComments.map(x => ({
            TestId: x.testId, Comments: x.comments.map(c => ({ CommentId: c.id, IsSelected: c.selected } as FixedCommentMutation)),
        }));
        const testAcceptability = acceptabilityState.spirometryResults.TestResults.map(x => ({ TestId: x.TestId, Accepted: x.OverreaderAcceptability ? true : false } as TestAcceptabilityMutation));

        try {
            const response = await legacyClient.updateAcceptabilityLungVolume(analysisSetId, decision, testAcceptability, sessionFixedCommentsMutation, testFixedCommentsMutation, annotation);

            if (response.AnalysisSetId !== analysisSetId) {
                setError("An error occurred saving the analysis set. Please contact an Administrator.");
            }

            goBackToList();
        }
        catch (ex) {
            setError(ex.Message);
            window.scrollTo(0, 0);
        }
    }

    const addNewNote = async (annotation: string) => {
        try {
            await legacyClient.putSessionNotes(analysisSet.SessionId, annotation);
        } catch (ex) {
            setNoteError(ex.Message);
            return;
        }
        await fetchSessionNotes(analysisSet.SessionId);
    }

    const editNote = async (id: string, comment: string, annotation: string) => {
        await client.client.patchSessionNote(
            {
                comment: comment,
            }, id, annotation);
        await fetchSessionNotes(analysisSet.SessionId);
    }

    const isOverread = viewState === "overread";
    const overreadingDisabled = isOverread && (fixedCommentStateOverreading.areAvailableFixedCommentsLoaded === false || (fixedCommentStateOverreading.areFixedCommentsLoaded === undefined || fixedCommentStateOverreading.areFixedCommentsLoaded === null || fixedCommentStateOverreading.areFixedCommentsLoaded === false));

    const hasAddingNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.AddLungVolumeSessionNotes);
    const hasViewNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.ViewLungVolumeSessionNotes);

    const pageAnalysisSet = sessionMetadata.AnalysisSets.find(x => x.Id === analysisSetId);
    const pageAnalysisSetType = pageAnalysisSet.Type.Id;
    const isQcAnalysisSet = pageAnalysisSetType === LungVolumeAnalysisSetTypeEnum.QC;
    const isSiteAnalysisSet = pageAnalysisSetType === LungVolumeAnalysisSetTypeEnum.Site;
    const isOverreadAnalysisSet = pageAnalysisSetType === LungVolumeAnalysisSetTypeEnum.Overread;
    const isUnauthorized = isQcAnalysisSet && pageAnalysisSet.Analyst.Username && pageAnalysisSet.Analyst.Username != currentUser.Username;

    if (isUnauthorized) {
        return <Navigate to={{ pathname: "/unauthorized" }} />;
    }

    if (isOverread && isSiteAnalysisSet) {
        return <Navigate to={{ pathname: `/LungVolume/Analysis/${analysisSetId}/view` }} />;
    }

    return (
        <>
            <Alert show={overreadingDisabled} category={AlertCategoryEnum.Danger}>
                Over-reading disabled. Fixed comments have not been configured for this study, please ask the Administrator to configure these first.
            </Alert>

            <Alert show={error !== undefined} category={AlertCategoryEnum.Danger}>
                {error}
            </Alert>

            {!isQcAnalysisSet && !isSiteAnalysisSet && !isViewOnly && sessionMetadata.ChallengeStatusId === OverreadChallengeStatusEnum.Challenged && !sessionMetadata.ChallengeIsClosed ?
                <ChallengeAlert
                    sessionId={sessionMetadata.Id}
                    sessionType={sessionType}
                    onRejectComplete={goBackToList}
                /> : null}

            <h2 className="text-primary">Lung Volume Data</h2>
            {!isQcAnalysisSet ? <SessionMetadataTable
                sessionMetadata={sessionMetadata}
                selectedAnalysisSetId={analysisSetId}
            /> : <Alert show={true} category={AlertCategoryEnum.Info}>Session Information is hidden from view in this workflow</Alert>}

            <h2 className="text-primary">Results</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <LungVolumeResultsTable
                        isRejected={isAnalysisRejected}
                        isViewOnly={isViewOnly || overreadingDisabled || acceptabilityState.loadingBestValues}
                        isSiteAnalysisSet={isSiteAnalysisSet}
                        resultData={acceptabilityState.spirometryResults as LungVolumeResults}
                        session={session}
                        fixedCommentsStateOverreading={fixedCommentStateOverreading}
                        fixedCommentsDispatchOverreading={fixedCommentDispatchOverreading}
                        shouldValidateFixedComments={shouldValidateFixedComments}
                        acceptabilityState={acceptabilityState}
                        acceptabilityDispatch={acceptabilityDispatch}
                        studyLungVolumeRelevantParameters={studyLungVolumeRelevantParameters}
                    />
                </div>
            </div>
            <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.AccessLVAnalysisSetsAttachment}>
                <h2 className="text-primary">Attachments</h2>
                <div className="panel panel-default">
                    <div className="panel-body">
                        <FileAttachmentsTable
                            legacyClient={legacyClient}
                            fileAttachments={analysisSet.Attachments.map(a => ({ id: a.Id, fileSizeInBytes: a.FileSizeInBytes, fileName: a.FileName }))}
                            mode={FileAttachmentsTableMode.ListOnly}
                        />
                    </div>
                </div>
            </Authorization>
            {
                hasViewNotePermission ?
                    <NotesPanel
                        buttonDisabled={sessionMetadata.AnalysisSets.find(a => a.Id === analysisSetId).Type.Id === LungVolumeAnalysisSetTypeEnum.QC}
                        header="Session Notes"
                        showNotesCount={true}
                        isAddingNotesEnabled={!isViewOnly && !overreadingDisabled && hasAddingNotePermission && isOverreadAnalysisSet}
                        notes={sessionNotes}
                        errorStatus={noteError}
                        modalSize={ModalSize.Large}
                        onEditNote={(id, comment, annotation) => editNote(id, comment, annotation)}
                        onAddNote={(annotation) => addNewNote(annotation)}
                    />
                    : null
            }
            <AcceptabilityDashboard
                isViewOnly={isViewOnly || overreadingDisabled}
                analysisSetId={analysisSetId}
                fixedCommentsDispatch={fixedCommentDispatchOverreading}
                fixedCommentState={fixedCommentStateOverreading}
                acceptabilityState={acceptabilityState}
                sessionType={sessionType}
                overreadStatus={sessionMetadata.AnalysisSets.find(a => a.Id === analysisSetId).Status.Id}
                onNoSessionCommentsSelected={() => {
                    fixedCommentDispatchOverreading(ToggleSessionFixedCommentsVisibilityAction.create());
                }}
                onAcceptabilitySelected={(annotation, decision) => setAnalysisDecision(annotation, decision)}
                onCancel={goBackToList}
            />
        </>
    );
};

export default LungVolumeAnalysisScreen;
